import type { WorkflowDto, WorkflowStepDto } from '../workflow';
import type { WorkflowEdge, WorkflowGraph } from './types';

/**
 * Map a xyflow graph back into a normalised WorkflowDto.
 *
 * - Control-flow edges (`data.kind === 'control'`) become `runAfter` entries.
 * - Data-flow edges (`data.kind === 'data'`) are visual only and dropped here
 *   (the binding lives in node data already).
 * - `data.parentStepId` is used to rebuild the `nestedSteps` tree.
 */
export const xyflowToWorkflow = (name: string, graph: WorkflowGraph): WorkflowDto => {
  const steps: Record<string, WorkflowStepDto> = {};
  const parentByStepId = new Map<string, string>();

  graph.nodes.forEach((node) => {
    const { data } = node;
    steps[data.stepId] = {
      id: data.stepId,
      actionId: data.actionId,
      input: { ...data.input },
      settings: { ...data.settings },
      runAfter: [],
    };

    if (data.parentStepId) {
      parentByStepId.set(data.stepId, data.parentStepId);
    }
  });

  const edgesByTarget = new Map<string, WorkflowEdge[]>();

  graph.edges.forEach((edge) => {
    if (!edge.data || edge.data.kind !== 'control') {
      return;
    }

    const list = edgesByTarget.get(edge.target) ?? [];
    list.push(edge);
    edgesByTarget.set(edge.target, list);
  });

  edgesByTarget.forEach((edgeList, targetNodeId) => {
    const step = steps[targetNodeId];
    if (!step) {
      return;
    }

    edgeList.forEach((edge) => {
      step.runAfter.push({
        stepId: edge.source,
        enableOnCondition: edge.data?.enableOnCondition,
        routeKey: edge.data?.routeKey,
      });
    });
  });

  // Rebuild nestedSteps structure based on parentStepId hints.
  const childIds = new Set<string>();
  parentByStepId.forEach((parentId, childId) => {
    const parent = steps[parentId];
    const child = steps[childId];
    if (!parent || !child) {
      return;
    }

    if (!parent.nestedSteps) {
      parent.nestedSteps = {};
    }

    parent.nestedSteps[childId] = child;
    childIds.add(childId);
  });

  const rootSteps: Record<string, WorkflowStepDto> = {};
  Object.entries(steps).forEach(([id, step]) => {
    if (!childIds.has(id)) {
      rootSteps[id] = step;
    }
  });

  return { name, steps: rootSteps };
};
